import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft, ShieldAlert } from 'lucide-react';
export function SpamGuidePage() {
  const [checklist, setChecklist] = useState([false, false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  const [scenarioAnswer, setScenarioAnswer] = useState<string | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist and scenario (up to 50% before completion)
    calculateProgress(newChecklist, scenarioAnswer);
  };
  const handleScenario = (answer: string) => {
    setScenarioAnswer(answer);
    setHasInteracted(true);
    calculateProgress(checklist, answer);
  };
  const calculateProgress = (currentChecklist: boolean[], currentScenario: string | null) => {
    if (isCompleted) return;
    let score = 0;
    const totalItems = currentChecklist.length + 1; // checklist items + scenario
    const completedChecks = currentChecklist.filter(Boolean).length;
    score += completedChecks;
    if (currentScenario !== null) {
      score += 1;
    }
    setProgress(Math.round(score / totalItems * 50));
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-5442" data-mp="1w1bccte46mro">
      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-5443" data-mp="3t1xjv9reb4j4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-5444" data-mp="3o2uji2md1mhh">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-5445" data-mp="5wzonnpha6yp">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-5446" data-mp="39f9do0y3p95v" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-5447" data-mp="35f90skqnh60x" />
        </div>
      </header>

      <main data-id="element-5448" data-mp="3ec9jcar1g9wg">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-5449" data-mp="2qn7419jvmt54">
          <div className="max-w-3xl mx-auto" data-id="element-5450" data-mp="2lodrbjcez660">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-5451" data-mp="2lnccd3sa1zj0">
              Reducing Spam and Junk Mail
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-5452" data-mp="15ywe2hy4iuhz">
              After completing this module, you'll know how to push back against
              spam across every channel using tools you already have on your
              phone and computer.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-5453" data-mp="1kwoq304gp0rf">
              <span aria-hidden="true" data-id="element-5454" data-mp="1axpg9e7ymz2u">⏱️</span> 10 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-5455" data-mp="1fw0q3oy999l2">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-5456" data-mp="3vneubp0uaguh">
                <span id="progress-label" data-id="element-5457" data-mp="3vprm0tweg5kq">Your progress</span>
                <span data-id="element-5458" data-mp="4hwwwkzhrph2">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-5459" data-mp="jqikg9vz7xoi">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-5460" data-mp="5zvsd0kq161e" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-5461" data-mp="tnn525a6bl37">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-5462" data-mp="oqn0jysz2qe7">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-5463" data-mp="3efp0os4wfb7d">
              📱
            </div>
            <div className="flex-1 space-y-4" data-id="element-5464" data-mp="2zdp5evizjba1">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-5465" data-mp="34az30ygj2t8y">
                David's phone buzzed for the fifth time during dinner. Another
                "urgent package delivery" text with a suspicious link. His inbox
                was overflowing with newsletters he never signed up for, and his
                physical mailbox was stuffed with credit card offers. He felt
                like he was constantly swatting away digital mosquitoes, but
                they just kept coming back.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-5466" data-mp="3s24kfi249tld">
                In this module, you'll learn exactly how to stop the noise and
                take back control of your inbox, phone, and mailbox using
                simple, built-in tools.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-5467" data-mp="1ulped77ub5bu">
          <div className="max-w-4xl mx-auto" data-id="element-5468" data-mp="1pmvzpbgth8a3">
            <h2 className="sr-only" data-id="element-5469" data-mp="1pnxenr0yeex3">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5470" data-mp="24jn11qa1wu2c">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5471" data-mp="1zk0o7hy4xfu2">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5472" data-mp="2digtr5k9h0am">
                  Training Your Email Filter
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5473" data-mp="2n9l9w8loar71">
                  Your email provider has built-in tools to report suspicious
                  messages. Every time you click "Report Spam" or "Report
                  Phishing" instead of just deleting an email, you train the
                  system's AI to catch similar junk before it ever reaches your
                  inbox.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5474" data-mp="3ayjf8n0909kh">
                  Think of this like teaching a guard dog which strangers to
                  bark at. If you just quietly throw the mail away, the dog
                  never learns.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5475" data-mp="36xhbbsvdz9c6">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5476" data-mp="2wy077fvs93ib">
                  Stopping Text Spam
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5477" data-mp="2j7wv3hqmaq6l">
                  You can report spam texts directly to your carrier by
                  forwarding the message to 7726 (which spells "SPAM" on your
                  keypad). Both iPhones and Androids also have built-in features
                  to filter unknown senders into a separate folder so they don't
                  interrupt your day.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5478" data-mp="2opnrbnv018lq">
                  Think of 7726 like a direct hotline to the digital bouncer at
                  your phone company.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5479" data-mp="2aq4kqzhu65bj">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5480" data-mp="25z5zdlhzqh1f">
                  Blocking Scam Calls
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5481" data-mp="20y8kpiw6w54o">
                  The National Do Not Call Registry stops legitimate
                  telemarketers, but scammers ignore it. To stop them, you need
                  to activate the free spam-blocking app provided by your
                  carrier (like AT&T ActiveArmor, Verizon Call Filter, or
                  T-Mobile Scam Shield).
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5482" data-mp="7h2te8o3aux1">
                  Think of the Do Not Call list as a "No Trespassing" sign, and
                  your carrier's app as the actual locked gate.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5483" data-mp="3fxxmiwf350xx">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5484" data-mp="3kxa87du801yx">
                  Halting Physical Junk Mail
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5485" data-mp="3tokc9z2vbghv">
                  You can significantly reduce unsolicited catalogs, credit card
                  offers, and marketing mailers by registering with DMAchoice, a
                  mail preference service run by the Data and Marketing
                  Association.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5486" data-mp="1emxzgmbrgbb7">
                  Think of DMAchoice as turning off the main water valve instead
                  of trying to catch every drip with a bucket.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-5487" data-mp="vvx3m75xsvvv">
          <div className="max-w-3xl mx-auto" data-id="element-5488" data-mp="zwqt34z12iqv">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-5489" data-mp="14v0ptk0ehrel">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-5490" data-mp="19msgdujk3x4q">
              Let's set up your defenses right now. Tap each step as you
              complete it on your devices.
            </p>

            <div className="space-y-4 mb-12" role="group" aria-label="Preparation Checklist" data-id="element-5491" data-mp="h44x8ko4es6a">
              {["Find the 'Report Spam' or 'Report Phishing' button in your email app so you know where it is.", "Save the number 7726 in your phone's contacts as 'Report SPAM'.", "Turn on 'Filter Unknown Senders' (iPhone) or 'Spam Protection' (Android) in your messaging app settings.", "Download and activate your carrier's free spam-blocking app (ActiveArmor, Call Filter, or Scam Shield)."].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-5492" data-mp="3d2nf23lxf9ed" data-mpi={i}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-5493" data-mp="2pe792upb8ld2">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-5494" data-mp="2kfdwd4hukydy" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-5495" data-mp="38587nhmiqew3">
                    {item}
                  </span>
                </button>)}
            </div>

            {/* Scenario Choice */}
            <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200" data-id="element-5496" data-mp="33e00ks8nv5m3">
              <div className="flex items-center gap-3 mb-6" data-id="element-5497" data-mp="3r4cdkhirjsjj">
                <ShieldAlert size={28} className="text-[#8528e2]" aria-hidden="true" data-id="element-5498" data-mp="3r2svcldjr7f4" />
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold" data-id="element-5499" data-mp="1kf2ceux21e7b">
                  Quick Check
                </h3>
              </div>
              <p className="font-inter text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-5500" data-mp="1t8nq0axhgpf5">
                You receive a text message from an unknown number saying your
                Netflix account has been suspended, with a link to "update your
                payment info." What is the best way to handle this?
              </p>

              <div className="space-y-3" data-id="element-5501" data-mp="wj6ehfbtk95j">
                <button onClick={() => handleScenario('A')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'A' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5502" data-mp="wk95rod20dlz">
                  A: Reply "STOP" to unsubscribe from the messages.
                </button>
                <button onClick={() => handleScenario('B')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'B' ? 'border-[#01ced1] bg-[#01ced1]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5503" data-mp="3c97vvuar7y6c">
                  B: Forward the message to 7726, then delete and report it as
                  junk.
                </button>
                <button onClick={() => handleScenario('C')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'C' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5504" data-mp="40hb4gug0no0">
                  C: Click the link just to see if it's a real Netflix page, but
                  don't enter any info.
                </button>
              </div>

              {scenarioAnswer === 'B' && <div className="mt-6 p-4 bg-[#01ced1]/10 border-l-4 border-[#01ced1] rounded-r-lg animate-in fade-in" data-id="element-5505" data-mp="237xsbu31hxw0">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5506" data-mp="1y8bepjjaufmo">
                    That's right! Forwarding to 7726 alerts your carrier, and
                    reporting it as junk trains your phone's filters. Never
                    reply to or click links in suspicious texts.
                  </p>
                </div>}
              {scenarioAnswer === 'A' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-5507" data-mp="2h75yj6xme69p">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5508" data-mp="2c7ktcerux1oj">
                    Not quite — replying "STOP" to a scammer actually confirms
                    your number is active, which will lead to more spam. Forward
                    it to 7726 instead.
                  </p>
                </div>}
              {scenarioAnswer === 'C' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-5509" data-mp="171l1c60ip6ed">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5510" data-mp="t954xo24g6mf">
                    Actually, clicking the link is dangerous. It can confirm
                    your number is active or even install malware on your
                    device. Forward it to 7726 instead.
                  </p>
                </div>}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-5511" data-mp="fa5c047kt5kg">
          <div className="max-w-3xl mx-auto" data-id="element-5512" data-mp="faovhpcvk20l">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-5513" data-mp="24pd0h5j4yhol">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-5514" data-mp="29opwafrziz3b">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5515" data-mp="2ntyjtxyoud1a">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-5516" data-mp="32imbvu9bs4mh">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-5517" data-mp="1d3y1fykaowr3">
          <div className="max-w-3xl mx-auto" data-id="element-5518" data-mp="1d5rb7mq7fx3b">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-5519" data-mp="21pedzsjst1d6">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-5520" data-mp="26mwbmmsd7u5v">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-5521" data-mp="2fepwumiu9t1s">
                Which type of spam bothers you the most, and which tool will you
                set up first to stop it?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Take a moment to think about this..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-5522" data-mp="1rqf2m27nermh" />
              <p className="font-inter text-gray-500 text-sm mt-4" data-id="element-5523" data-mp="3iag9fm72wz6n">
                This is just for you — your response isn't stored or shared.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-5524" data-mp="34gfk1j20ooiy">
          <div className="max-w-3xl mx-auto text-center" data-id="element-5525" data-mp="2zie0kx0oin6y">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-5526" data-mp="2zfrhc04fej8w">
              The single most important action you can take today is to register
              your number on the Do Not Call Registry and turn on your carrier's
              spam blocking app.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-5527" data-mp="2lkpghrefrk2y">
              <button onClick={() => window.print()} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-5528" data-mp="nrs5zravpmuc">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5529" data-mp="2ihmutpbtphxr" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5530" data-mp="28qs70gixiaa7">
                  Print
                </span>
              </button>
              <a href="mailto:?subject=Helpful guide on reducing spam&body=I found this guide on reducing spam and junk mail really helpful. You should check it out." className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 no-underline text-white" data-id="element-5531" data-mp="23sqnxvli6ayq">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5532" data-mp="3p3pfnigjhvh0" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5533" data-mp="3k5nwyyn0zy62">
                  Email to myself
                </span>
              </a>
              <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-5534" data-mp="3rncamyxhe7vo">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5535" data-mp="38r3zxflp94sz" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5536" data-mp="3drrrq3hr5po9">
                  Copy link
                </span>
              </button>
              <button title="Coming soon" className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 cursor-not-allowed opacity-70" data-id="element-5537" data-mp="3mqvd5e4e6h5x">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5538" data-mp="2z094qy3vx8nh" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5539" data-mp="2u1ec74g485q6">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-5540" data-mp="2fc03lykd5sbu">
            <div className="max-w-3xl mx-auto text-center" data-id="element-5541" data-mp="2bcr9gslevsfr">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-5542" data-mp="2p34ccgjg4gy8">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-5543" data-mp="2kb2v0g7f801c" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-5544" data-mp="1rnqhswn5h4kp">
                Nice work. The Spam Reduction module is complete. Your inbox is
                a little quieter today.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-5545" data-mp="bx6877srpy9d">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-5546" data-mp="33b9ahi8p426">
                  Know someone who could use this?
                </p>
                <a href="mailto:?subject=Helpful guide on reducing spam&body=I found this guide on reducing spam and junk mail really helpful. You should check it out." className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto no-underline" data-id="element-5547" data-mp="qtnevlq0iopl">
                  <Share2 size={20} aria-hidden="true" data-id="element-5548" data-mp="36k09llr8bhje" />
                  Send to a family member
                </a>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-5549" data-mp="3luq4k2ktn6vv">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-5550" data-mp="3h2omg00z2lxx">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-5551" data-mp="384cwm07xjs6j">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-5552" data-mp="4ljk7qqw19dy">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-5553" data-mp="3vruijf4ijw1l">
                      Back to Resources
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-5554" data-mp="3qsroepq2na8e">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-5555" data-mp="2fcjge8rvf6pc" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}